<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background Image with Dark Transparent Overlay -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    ></div>
    <div class="absolute inset-0 bg-black bg-opacity-70"></div>

    <!-- Content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div class="w-full max-w-3xl">
        <!-- Card -->
        <div>
          <!-- Brand Logo -->
          <div class="text-center mb-8">
            <img
              v-if="currentBrand"
              :src="currentBrand.formLogo"
              :alt="currentBrand.name"
              class="mx-auto w-120 object-contain"
            />
            <h2 class="text-5xl font-medium" :style="{ color: currentBrand?.color }">
              {{ isFalken ? 'Road Hazard Ready' : 'TOTAL CONFIDENCE PLAN' }}
            </h2>
            <p class="mt-6 mb-4 text-2xl text-white">
              Reset Your Password
            </p>
            <p class="text-lg text-white opacity-90">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          <!-- Success Message -->
          <div v-if="success" class="mb-6 rounded-md bg-green-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-green-700">{{ success }}</p>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="mb-6 rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Forgot Password Form -->
          <form @submit.prevent="handleForgotPassword" class="space-y-6">
            <!-- Email -->
            <div>
              <input
                id="email"
                v-model="email"
                type="email"
                placeholder="Email Address*"
                required
                class="w-full px-8 py-5 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
            </div>

            <!-- Submit Button -->
            <div>
              <button
                type="submit"
                :disabled="loading"
                class="w-full py-5 px-4 rounded-lg text-white text-xl font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                :style="{ backgroundColor: currentBrand?.color }"
              >
                {{ loading ? 'Sending...' : 'Send Reset Link' }}
              </button>
            </div>

            <!-- Back to Login Link -->
            <div class="text-center">
              <a
                href="#"
                @click.prevent="goToLogin"
                class="text-lg font-medium hover:underline"
                :style="{ color: currentBrand?.color }"
              >
                Back to Sign In
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useForgotPassword } from '@/composables/auth/useForgotPassword'
import { getBrandBySlug } from '@/config/brands'

const route = useRoute()
const router = useRouter()

const currentBrand = computed(() => {
  const brandSlug = route.params.brandSlug as string
  return getBrandBySlug(brandSlug)
})

const isFalken = computed(() => currentBrand.value?.name?.toLowerCase() === 'falken')

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backgroundImage = require('@/assets/staticbg.png')

const { email, loading, error, success, handleForgotPassword } = useForgotPassword()

const goToLogin = () => {
  router.push(`/login/${currentBrand.value?.slug}`)
}

// Redirect if no brand slug
onMounted(() => {
  if (!currentBrand.value) {
    router.push('/brands')
  }
})
</script>

<style scoped>
.brand-input:focus {
  border-color: var(--focus-color);
  box-shadow: 0 0 0 3px rgba(255, 166, 0, 0.1);
}
</style>

